import { StrictMode, Suspense, lazy } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Route, Routes } from 'react-router-dom'

import './styles/tailwind.css'
import { AppShell } from '@/components/AppShell'
import { LandingPage } from '@/features/landing/LandingPage'
import { DashboardPage } from '@/features/dashboard/DashboardPage'
import { AufmassPage } from '@/features/aufmass/AufmassPage'
import { ProjectsPage } from '@/features/projects/ProjectsPage'
import { ProjectDetailPage } from '@/features/projects/ProjectDetailPage'
import { BonusPage } from '@/features/bonus/BonusPage'
import { UsersPage } from '@/features/users/UsersPage'
import { MobileUsersPage } from '@/features/mobile-users/MobileUsersPage'
import { ReconciliationPage } from '@/features/reconciliation/ReconciliationPage'
import { ChangeLogPage } from '@/features/changelog/ChangeLogPage'
import { CrewsPage } from '@/features/crews/CrewsPage'
import { FleetPage } from '@/features/fleet/FleetPage'
import { VehiclePage } from '@/features/fleet/VehiclePage'
import { PortfolioPage } from '@/features/execution/PortfolioPage'
import { MyWorkPage } from '@/features/workflow/MyWorkPage'
import { PeoplePage } from '@/features/people/PeoplePage'
import { BillingPage } from '@/features/billing/BillingPage'
import { ContractPage } from '@/features/billing/ContractPage'
import { InventoryPage } from '@/features/inventory/InventoryPage'
import { DrumPage } from '@/features/inventory/DrumPage'
import { LabelsPage } from '@/features/inventory/LabelsPage'
import { ReconciliationPage as CableReconciliationPage } from '@/features/inventory/ReconciliationPage'
import { CrewDetailPage } from '@/features/crews/CrewDetailPage'
import { Landing, RequireAdmin, RequireCap } from '@/components/access'
import { RouteLoader } from '@/components/LogoLoader'
import { I18nProvider, useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { appMessages } from '@/i18n/messages/app'
import { useMe } from '@/features/auth/useAuth'
import { ApiError, Capabilities } from '@/api/client'

// MapLibre is ~200 kB and only the RTK screen needs it. Loading it lazily keeps it out of
// the bundle every other page pays for.
const RtkPage = lazy(() =>
  import('@/features/rtk/RtkPage').then((m) => ({ default: m.RtkPage })))
const FleetMapPage = lazy(() =>
  import('@/features/fleet/FleetMapPage').then((m) => ({ default: m.FleetMapPage })))

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      // Retrying a 401 or 403 just delays showing the user what happened.
      retry: (failureCount, error) =>
        error instanceof ApiError && (error.isUnauthorized || error.isForbidden)
          ? false
          : failureCount < 2,
    },
  },
})

function App() {
  const { data: me, isPending } = useMe()
  const tc = useT(commonMessages)
  const t = useT(appMessages)

  if (isPending) {
    return <div className="grid min-h-screen place-items-center text-sm text-neutral-500">{tc('loading')}</div>
  }

  if (!me) {
    return <LandingPage />
  }

  return (
    <Routes>
      <Route element={<AppShell />}>
        {/* Land on the first page the user is allowed to see (or a no-access screen). */}
        <Route index element={<Landing />} />

        <Route path="/aufmass" element={<RequireCap cap={Capabilities.ViewAufmassGrid}><AufmassPage /></RequireCap>} />
        <Route path="/projects" element={<RequireCap cap={Capabilities.ViewProjects}><ProjectsPage /></RequireCap>} />
        <Route path="/projects/:id" element={<RequireCap cap={Capabilities.ViewProjects}><ProjectDetailPage /></RequireCap>} />
        <Route path="/bonus" element={<RequireCap cap={Capabilities.ViewBonus}><BonusPage /></RequireCap>} />
        <Route path="/users" element={<RequireCap cap={Capabilities.ManageUsers}><UsersPage /></RequireCap>} />
        <Route path="/mobile-users" element={<RequireCap cap={Capabilities.ManageUsers}><MobileUsersPage /></RequireCap>} />
        <Route
          path="/rtk"
          element={
            <RequireCap cap={Capabilities.ViewRtk}>
              <Suspense fallback={<RouteLoader label={t('loadingMap')} />}>
                <RtkPage />
              </Suspense>
            </RequireCap>
          }
        />
        <Route path="/dashboard" element={<RequireCap cap={Capabilities.ViewDashboard}><DashboardPage /></RequireCap>} />
        {/* Reconciliation stays hidden — admin only for now. */}
        <Route path="/reconciliation" element={<RequireCap cap={Capabilities.ManageUsers}><ReconciliationPage /></RequireCap>} />
        {/* Change Log — the audit trail of every edit. Admin only. */}
        <Route path="/inventory" element={<RequireCap cap={Capabilities.ViewInventory}><InventoryPage /></RequireCap>} />
        <Route path="/inventory/reconciliation" element={<RequireCap cap={Capabilities.ViewInventory}><CableReconciliationPage /></RequireCap>} />
        <Route path="/inventory/labels" element={<RequireCap cap={Capabilities.ViewInventory}><LabelsPage /></RequireCap>} />
        {/* The address every drum label's QR encodes. */}
        <Route path="/d/:code" element={<RequireCap cap={Capabilities.ViewInventory}><DrumPage /></RequireCap>} />
        <Route
          path="/fleet"
          element={
            <RequireCap cap={Capabilities.ViewFleet}>
              <Suspense fallback={<RouteLoader label={t('loadingMap')} />}>
                <FleetMapPage />
              </Suspense>
            </RequireCap>
          }
        />
        <Route path="/fleet/list" element={<RequireCap cap={Capabilities.ViewFleet}><FleetPage /></RequireCap>} />
        <Route path="/billing" element={<RequireCap cap={Capabilities.ViewBilling}><BillingPage /></RequireCap>} />
        <Route path="/billing/:id" element={<RequireCap cap={Capabilities.ViewBilling}><ContractPage /></RequireCap>} />
        <Route path="/people" element={<RequireCap cap={Capabilities.ViewPeople}><PeoplePage /></RequireCap>} />
        <Route path="/my-work" element={<RequireCap cap={Capabilities.ViewProjects}><MyWorkPage /></RequireCap>} />
        <Route path="/portfolio" element={<RequireCap cap={Capabilities.ViewProjects}><PortfolioPage /></RequireCap>} />
        <Route
          path="/fleet/map"
          element={
            <RequireCap cap={Capabilities.ViewFleet}>
              <Suspense fallback={<RouteLoader label={t('loadingMap')} />}>
                <FleetMapPage />
              </Suspense>
            </RequireCap>
          }
        />
        <Route path="/fleet/:id" element={<RequireCap cap={Capabilities.ViewFleet}><VehiclePage /></RequireCap>} />
        <Route path="/crews" element={<RequireAdmin><CrewsPage /></RequireAdmin>} />
        <Route path="/crews/:id" element={<RequireAdmin><CrewDetailPage /></RequireAdmin>} />
        <Route path="/changelog" element={<RequireAdmin><ChangeLogPage /></RequireAdmin>} />

        <Route path="*" element={<Landing />} />
      </Route>
    </Routes>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </I18nProvider>
    </QueryClientProvider>
  </StrictMode>,
)
